import { useState } from "react";
import { type RouteComponentProps } from "react-router";

import { cn } from "@btnet/utilities-core";

import { type ListMetadata } from "utilities/list/list.types";
import { getBaseRoute, routes } from "utilities/routes";
// eslint-disable-next-line no-restricted-imports
import { getMobileOS } from "utilities/userAgent/userAgent";

import { BTLoading } from "commonComponents/utilities/BTLoading/BTLoading";
import { type IHeaderInfoResponse } from "commonComponents/utilities/HeaderInfo/OwnerHeaderInfo.api.types";
import {
    type AccountSwitcherItem,
    type JobPickerJob,
    type JobPickerOwnerInfo,
    type JobPickerPermissions,
} from "commonComponents/utilities/JobPicker/JobPicker.api.types";
import { useJobPickerJobList } from "commonComponents/utilities/JobPicker/JobPicker.sort";
import {
    JobIdTypes,
    type JobPickerDisplayModes,
    JobPickerSelectModes,
    JobSortOptions,
} from "commonComponents/utilities/JobPicker/JobPicker.types";
import { getAddJobLink } from "commonComponents/utilities/JobPicker/JobPicker.utils";
import { JobPickerEmptyStateBanner } from "commonComponents/utilities/JobPicker/JobPickerEmptyState/JobPickerEmptyStateBanner";
import { ContactInfoDropdown } from "commonComponents/utilities/JobPicker/Sections/ContactInfoDropdown/ContactInfoDropdown";
import { JobInfoSection } from "commonComponents/utilities/JobPicker/Sections/JobInfoSection/JobInfoSection";
import { JobPickerAccountSwitcher } from "commonComponents/utilities/JobPicker/Sections/JobPickerAccountSwitcher/JobPickerAccountSwitcher";
import { JobPickerFiltersPopover } from "commonComponents/utilities/JobPicker/Sections/JobPickerFiltersPopover/JobPickerFiltersPopover";
import { JobPickerLogo } from "commonComponents/utilities/JobPicker/Sections/JobPickerLogo/JobPickerLogo";
import { JobPickerResizeHandle } from "commonComponents/utilities/JobPicker/Sections/JobPickerResizeHandle/JobPickerResizeHandle";
import { JobPickerSegmentedControl } from "commonComponents/utilities/JobPicker/Sections/JobPickerSegmentedControl/JobPickerSegmentedControl";
import { JobPickerSortPopover } from "commonComponents/utilities/JobPicker/Sections/JobPickerSortPopover/JobPickerSortPopover";
import { JobSearchInput } from "commonComponents/utilities/JobPicker/Sections/JobSearchInput/JobSearchInput";
import { JobPickerJobsList } from "commonComponents/utilities/JobPicker/Sections/JobsList/JobsList";
import { PleaseSelectJobHeader } from "commonComponents/utilities/JobPicker/Sections/PleaseSelectJobHeader/PleaseSelectJobHeader";
import { useJobPickerDrawer } from "commonComponents/utilities/JobPicker/useJobPickerDrawer";
import { NewJobPrompt } from "commonComponents/utilities/NewJobPrompt/NewJobPrompt";
import { ShowOnPortal } from "commonComponents/utilities/ShowOnPortal/ShowOnPortal";

import {
    type FilterEntity,
    type IFilterFormValues,
    type SelectedFilterItem,
} from "entity/filters/Filter/Filter.api.types";
import { type JobRunningTotal } from "entity/job/Job.api.types";
import { useScheduledDraftsForJobPicker } from "entity/schedule/ScheduleDrafts/useDraftManager";
import { TemplateListTabs } from "entity/template/TemplateList/TemplateListTab/TemplateListTab.api.types";

import "./JobPicker.less";

export interface IJobPickerProps extends RouteComponentProps {
    onJobPickerJobChanged: (job: JobPickerJob, keywordSearch: string) => Promise<void>;
    onJobPickerBuilderChanged: (builderId: number) => Promise<void>;
    selectedJobIds: number[] | number | null;
    jobs: JobPickerJob[];
    jobRunningTotal?: JobRunningTotal;
    listMetadata: ListMetadata;

    availableAccounts: AccountSwitcherItem[];

    onAccountChange?: (newAccounts: AccountSwitcherItem[]) => Promise<void>;
    sortOption?: JobSortOptions;
    filters: FilterEntity;
    selectedFilters: SelectedFilterItem[] | undefined;
    selectedFilterId?: number;
    displayMode: JobPickerDisplayModes;
    templatesOnly: boolean;
    setCollapsed: (isCollapsing: boolean) => void;
    collapsed: boolean;
    onFilterSubmit: (filterValues: IFilterFormValues, keyword: string) => Promise<void>;
    onSavedFiltersUpdated: (filters: FilterEntity) => void;
    permissions: JobPickerPermissions;
    ownerInfo: JobPickerOwnerInfo;
    onTemplateModeChanged: (
        templatesOnly: boolean,
        builderId: number,
        initialTemplateTab?: TemplateListTabs,
    ) => Promise<void>;
    onSortChanged: (sortOption: JobSortOptions) => Promise<void>;
    selectMode: JobPickerSelectModes;
    keywordSearch: string;
    onKeywordClear: () => Promise<void>;
    onDismissBanner: () => Promise<void>;
    loading: boolean;
    onSavedFilterVisibleChange?: (visible: boolean) => void;
    areSavedFiltersShowing: boolean;
    className?: string;
    headerInfo?: IHeaderInfoResponse;
    summaryUrl?: string;
    width: number;
    isConnectedToAccounting: boolean;
    showAccountingOnTimeClock: boolean;
    builderId: number;
    userId: number;
    jobId: number;
    fromReact: boolean;
    isTemplateMode: boolean;
    onResizeWindow?: (collapsed: boolean) => void;
    allJobsText?: string;
}

export const JobPicker = (props: IJobPickerProps) => {
    const [keywordSearch, setKeywordSearch] = useState(props.keywordSearch);
    const [showSortPopover, setShowSortPopover] = useState(false);
    const [showFilterPopover, setShowFilterPopover] = useState(false);
    const [showMoreThanOneHundred, setShowMoreThanOneHundred] = useState(false);
    const [isEmptyStateBannerDismissed, setIsEmptyStateBannerDismissed] = useState(
        !props.listMetadata?.emptyStatesMetadata?.isNewToEntity,
    );

    const { sortOption, templatesOnly, setCollapsed, collapsed, builderId, displayMode } = props;
    const draftJobIds = useScheduledDraftsForJobPicker(builderId, displayMode);

    const {
        width,
        viewportWidth,
        drawerOpen,
        initialized,
        useFullWidth,
        isMobile,
        handleResize,
        handleToggleCollapse,
        handleDrawerOpen,
        startDrawerClose,
    } = useJobPickerDrawer({
        collapsed,
        setCollapsed,
        initialWidth: props.width,
        onResizeWindow: props.onResizeWindow,
    });

    const effectiveSortOption =
        sortOption !== undefined && !templatesOnly ? sortOption : JobSortOptions.Alphabetic;

    const hasJobOtherThanAllListed =
        props.jobs.length > 0 &&
        (props.jobs.length > 1 || props.jobs[0].jobId !== Number(JobIdTypes.AllJobs));

    const jobList = useJobPickerJobList({
        jobs: props.jobs,
        keywordSearch,
        templatesOnly,
        allJobsText: props.allJobsText,
        effectiveSortOption,
        showMoreThanOneHundred,
    });

    const onRecommendedTemplatesClick = async () => {
        if (props.isTemplateMode) {
            window.location.assign(
                getBaseRoute() + routes.template.getListLink(TemplateListTabs.RecommendedTemplates),
            );
        } else {
            await props.onTemplateModeChanged(
                true,
                builderId,
                TemplateListTabs.RecommendedTemplates,
            );
        }
    };

    const showNewJobPromptButton = props.permissions.canAddJobs;
    const showPleaseSelectJobHeader =
        (props.selectMode === JobPickerSelectModes.Single && props.selectedJobIds === null) ||
        props.selectedJobIds === JobIdTypes.NoJobs;

    const innerContent = (
        <>
            <div className="JobPickerHeader flex flex-col gap-4 px-4">
                {/* While JobPickerHeader is not used in CSS, it is used in the jobpicker wrapper to get the height and fix any scrollbar issues, keep this class here */}
                <div className="flex flex-row justify-between items-center pt-4">
                    <div className="flex flex-row items-center gap-2 min-w-0">
                        {(props.headerInfo?.mobileLogoPath || props.headerInfo?.logoPath) && (
                            <JobPickerLogo
                                url={props.headerInfo.mobileLogoPath || props.headerInfo.logoPath!}
                            />
                        )}
                        {!props.loading && (
                            <JobPickerAccountSwitcher
                                availableAccounts={props.availableAccounts}
                                onAccountChange={props.onAccountChange}
                            />
                        )}
                    </div>
                    <ShowOnPortal
                        subs
                        render={() =>
                            props.headerInfo?.builderName && (
                                <ContactInfoDropdown
                                    history={props.history}
                                    match={props.match}
                                    location={props.location}
                                    parentUrl={props.match.url}
                                    jobId={props.jobId}
                                />
                            )
                        }
                    />
                </div>

                {(hasJobOtherThanAllListed ||
                    props.listMetadata?.emptyStatesMetadata?.hasFilteredData ||
                    keywordSearch) && (
                    <JobInfoSection
                        selectedJobIds={props.selectedJobIds}
                        jobs={props.jobs}
                        permissions={props.permissions}
                        ownerInfo={props.ownerInfo}
                        templatesOnly={props.templatesOnly}
                        isTemplateMode={props.isTemplateMode}
                    />
                )}

                {/* Job picker controls | Segmented control, + Job/Template Button, Search, Filters, and Sorting */}
                <div className="flex flex-col gap-4 w-full">
                    <ShowOnPortal
                        builder
                        render={() => (
                            <div className="flex flex-row w-full items-center justify-between">
                                <JobPickerSegmentedControl
                                    templatesOnly={props.templatesOnly}
                                    onTemplateModeChanged={props.onTemplateModeChanged}
                                    builderId={props.builderId}
                                />
                                {showNewJobPromptButton && (
                                    <NewJobPrompt
                                        jobRoute={getAddJobLink(templatesOnly, true, true)}
                                        templateRoute={getAddJobLink(templatesOnly, false, true)}
                                        block
                                        newName={props.isTemplateMode ? "Template" : "Job"}
                                        onRecommendedTemplatesClick={onRecommendedTemplatesClick}
                                        isTemplateMode={props.isTemplateMode}
                                    />
                                )}
                            </div>
                        )}
                    />

                    <div
                        className={cn("flex flex-row w-full items-center gap-4 ", {
                            "pb-2": !showPleaseSelectJobHeader,
                        })}
                    >
                        <div className="flex flex-row w-full items-center justify-start">
                            {(hasJobOtherThanAllListed ||
                                props.listMetadata?.emptyStatesMetadata?.hasFilteredData ||
                                keywordSearch) && (
                                <JobSearchInput
                                    committedKeyword={props.keywordSearch}
                                    localKeyword={keywordSearch}
                                    setLocalKeyword={setKeywordSearch}
                                    onKeywordClear={props.onKeywordClear}
                                    fromReact={props.fromReact}
                                />
                            )}
                        </div>
                        <div className="flex flex-row items-center justify-end gap-2">
                            <JobPickerFiltersPopover
                                templatesOnly={props.templatesOnly}
                                filters={props.filters}
                                selectedFilters={props.selectedFilters}
                                selectedFilterId={props.selectedFilterId}
                                areSavedFiltersShowing={props.areSavedFiltersShowing}
                                onSavedFiltersUpdated={props.onSavedFiltersUpdated}
                                onSavedFilterVisibleChange={props.onSavedFilterVisibleChange}
                                onFilterSubmit={async (filterValues: IFilterFormValues) => {
                                    return await props.onFilterSubmit(filterValues, keywordSearch);
                                }}
                                showPopover={showFilterPopover}
                                onVisibleChange={setShowFilterPopover}
                                sortOption={effectiveSortOption}
                                shouldCloseOnScroll={getMobileOS() !== "iOS"}
                            />
                            {!props.templatesOnly && (
                                <JobPickerSortPopover
                                    onSortChanged={props.onSortChanged}
                                    sortOption={effectiveSortOption}
                                    showPopover={showSortPopover}
                                    onVisibleChange={setShowSortPopover}
                                />
                            )}
                        </div>
                    </div>
                </div>

                {showPleaseSelectJobHeader && (
                    <div className="JobPickerPromptScrollbarGutter">
                        <PleaseSelectJobHeader templatesOnly={props.templatesOnly} />
                    </div>
                )}

                {props.listMetadata && (
                    <JobPickerEmptyStateBanner
                        actionBeingPerformed={undefined}
                        hasFilteredData={props.listMetadata.emptyStatesMetadata?.hasFilteredData}
                        hasListData={props.listMetadata.hasData}
                        isDismissed={isEmptyStateBannerDismissed}
                        isNewToEntity={props.listMetadata.emptyStatesMetadata?.isNewToEntity}
                        isReadonly={props.listMetadata.emptyStatesMetadata?.isReadOnly}
                        onCallToActionClick={() => {}}
                        onDismiss={async () => {
                            if (props.onDismissBanner) {
                                await props.onDismissBanner();
                            }
                            setIsEmptyStateBannerDismissed(true);
                        }}
                        canClearSearch={false}
                        isBanner
                        mode={props.templatesOnly ? "Template" : "Job"}
                        isConnectedToAccounting={props.isConnectedToAccounting}
                    />
                )}
            </div>
            {props.loading && (
                <div className="relative h-full" style={{ width: props.width }}>
                    <BTLoading displayMode="absolute" />
                </div>
            )}
            {!props.loading &&
                (hasJobOtherThanAllListed ||
                    props.listMetadata.emptyStatesMetadata.hasFilteredData ||
                    keywordSearch) && (
                    <JobPickerJobsList
                        jobsListAndCount={jobList}
                        selectedJobIds={props.selectedJobIds}
                        displayMode={props.displayMode}
                        isTemplateMode={props.isTemplateMode}
                        onJobPickerJobChanged={props.onJobPickerJobChanged}
                        availableAccounts={props.availableAccounts}
                        keywordSearch={keywordSearch}
                        draftJobIds={draftJobIds}
                        showAccountingOnTimeClock={props.showAccountingOnTimeClock}
                        loading={props.loading}
                        templatesOnly={props.templatesOnly}
                        isConnectedToAccounting={props.isConnectedToAccounting}
                        jobs={props.jobs}
                        listMetadata={props.listMetadata}
                        clearKeywordSearch={async () => {
                            setKeywordSearch("");
                            if (props.keywordSearch !== "") {
                                await props.onKeywordClear();
                            }
                        }}
                        onShowMoreThanOneHundred={() => setShowMoreThanOneHundred(true)}
                    />
                )}
        </>
    );

    let barWidth: number | undefined;
    if (collapsed) {
        if (useFullWidth) {
            barWidth = viewportWidth;
        } else {
            barWidth = drawerOpen ? width + 5 : width;
        }
    }

    let jobPickerMarginLeft: number | undefined;
    if (collapsed && !drawerOpen) {
        jobPickerMarginLeft = -(width - 5) + 12;
    }

    const mouseProps = isMobile
        ? undefined
        : { onMouseMove: handleDrawerOpen, onMouseLeave: startDrawerClose };

    return (
        <div
            className={cn("ResizableJobPickerContainer flex", { Drawer: drawerOpen })}
            style={{
                width: barWidth,
                marginLeft: jobPickerMarginLeft,
                display: initialized ? undefined : "none",
            }}
            {...mouseProps}
        >
            <div
                id="reactJobPicker"
                className="JobPickerContainer ResizableJobPicker sticky max-h-full w-full flex items-stretch overflow-hidden"
            >
                {/* min-w-0 + overflow-x-hidden prevent long builder names / job addresses
                    from pushing the sidebar past its inline width and triggering horizontal
                    page scroll. */}
                <div
                    data-testid="job-picker"
                    className="JobPickerReact flex flex-col bg-white border-0 min-w-0 overflow-x-hidden"
                    style={{ width: useFullWidth ? viewportWidth : width }}
                >
                    {innerContent}
                </div>
            </div>
            <JobPickerResizeHandle
                collapsed={collapsed}
                setCollapsed={handleToggleCollapse}
                onResize={handleResize}
                drawerOpen={drawerOpen}
            />
        </div>
    );
};
